import type { AskNobiServerStreamChunk } from "./types";

interface StreamCallbacks {
  onChunk?: (chunk: AskNobiServerStreamChunk) => void;
  onDone?: () => void;
  onError?: (error: Error) => void;
}

export async function readAskNobiStream({
  request,
  callbacks,
}: {
  request: () => Promise<Response>;
  callbacks: StreamCallbacks;
}): Promise<void> {
  try {
    const response = await request();

    if (!response.ok || !response.body) {
      callbacks.onError?.(new Error("Failed to fetch AI response."));

      return;
    }

    // Process the actual stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          // Process any remaining buffer content
          if (buffer.trim()) {
            try {
              callbacks.onChunk?.(JSON.parse(buffer.trim()));
            } catch (error) {
              console.error("Error parsing final buffer content:", buffer, error);
            }
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf("\n")) >= 0) {
          const line = buffer.substring(0, newlineIndex).trim();
          buffer = buffer.substring(newlineIndex + 1);
          if (line) {
            try {
              callbacks.onChunk?.(JSON.parse(line));
            } catch (error) {
              console.error("Error parsing stream chunk:", line, error);
            }
          }
        }
      }
      callbacks.onDone?.();
    } catch (streamError) {
      callbacks.onError?.(streamError instanceof Error ? streamError : new Error("Stream reading failed."));
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    callbacks.onError?.(error instanceof Error ? error : new Error("Failed to send message"));
  }
}
